import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { WeatherApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/weather/weather-api';
import {
  WeatherOutlookDayResponse,
  WeatherOutlookResponse,
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/weather-outlook.response';
import { HeaderPaddingSafeAreaComponent } from '../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { SkeletonLoaderComponent } from '../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';

/** Habillage de chaque verdict météo. Le serveur décide, l'app dessine. */
const CONDITION_STYLE: Record<
  string,
  { emoji: string; bg: string; ink: string; bar: string }
> = {
  FROST: { emoji: '❄️', bg: '#E7F0FB', ink: '#1B4C7E', bar: '#7EA8D9' },
  HOT: { emoji: '🔥', bg: '#FDEEE4', ink: '#8A3B12', bar: '#F09A6A' },
  RAIN: { emoji: '🌧️', bg: '#E4EEFB', ink: '#1F4E85', bar: '#6E9BD6' },
  DRY: { emoji: '☀️', bg: '#FDF6E0', ink: '#7A5C09', bar: '#E9C75A' },
  WIND: { emoji: '💨', bg: '#EDF1F1', ink: '#3E5250', bar: '#9BB0AD' },
  MILD: { emoji: '🌤️', bg: '#F1F7F3', ink: '#2C5C46', bar: '#9FC9B2' },
};

interface DayView extends WeatherOutlookDayResponse {
  weekday: string;
  dayNumber: string;
  isToday: boolean;
  isPast: boolean;
}

/**
 * Détail météo sur la fenêtre utile au jardin.
 *
 * La carte d'accueil ne montre que le jour même : elle ne répond pas à la
 * seule question qui compte pour arroser ou protéger — « et après ? ».
 *
 * Aucun seuil n'est calculé ici : c'est le serveur qui qualifie chaque
 * journée, avec les mêmes valeurs que les conseils du jour. Recalculer côté
 * front garantirait une divergence tôt ou tard.
 */
@Component({
  selector: 'vw-weather-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HeaderPaddingSafeAreaComponent,
    SkeletonLoaderComponent,
  ],
  templateUrl: './weather-detail.component.html',
  providers: [{ provide: WeatherApi, useClass: WeatherApi }],
})
export class WeatherDetailComponent implements OnInit {
  private readonly weatherApi = inject(WeatherApi);
  private readonly location = inject(Location);

  outlook: WeatherOutlookResponse | null = null;
  days: DayView[] = [];
  isLoading = true;
  failed = false;

  async ngOnInit(): Promise<void> {
    try {
      this.outlook = await this.weatherApi.getOutlook();
      const todayIso = new Date().toISOString().slice(0, 10);
      this.days = (this.outlook?.days ?? []).map((d) => {
        const date = new Date(d.date + 'T12:00:00');
        return {
          ...d,
          weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
          dayNumber: String(date.getDate()),
          isToday: d.date === todayIso,
          isPast: d.date < todayIso,
        };
      });
    } catch {
      this.failed = true;
    } finally {
      this.isLoading = false;
    }
  }

  goBack(): void {
    this.location.back();
  }

  style(condition: string) {
    return CONDITION_STYLE[condition] ?? CONDITION_STYLE['MILD'];
  }

  /** Hauteur de la barre de pluie, relative au jour le plus arrosé. */
  rainHeight(day: DayView): number {
    const max = Math.max(...this.days.map((d) => d.precipitation ?? 0), 1);
    return Math.round(((day.precipitation ?? 0) / max) * 100);
  }

  /**
   * Dans combien de jours la prochaine pluie ? `null` si rien n'est annoncé.
   *
   * Un cumul seul — « 16 mm à venir » — ne dit pas s'il faut arroser ce soir
   * ou attendre. C'est l'échéance qui décide du geste.
   */
  get nextRainInDays(): number | null {
    const todayIso = new Date().toLocaleDateString('en-CA');
    const next = this.days.find(
      (d) => d.date >= todayIso && (d.precipitation ?? 0) >= 1,
    );
    if (!next) return null;
    const start = new Date(todayIso + 'T12:00:00').getTime();
    const target = new Date(next.date + 'T12:00:00').getTime();
    return Math.round((target - start) / 86_400_000);
  }

  /** Le prochain jour qui demande une action, s'il y en a un. */
  get nextNotableDay(): DayView | null {
    return (
      this.days.find(
        (d) => !d.isPast && ['FROST', 'HOT', 'RAIN'].includes(d.condition),
      ) ?? null
    );
  }
}
